import moment from "moment";
import React from "react";
import InlineSVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { DialogHeader, DialogTitle } from "src/sudokit/components/Dialog";
import { PartnerCode } from "./PartnerCodesQueries";

interface PartnerCodeDetailsProps {
  code: PartnerCode;
  onClose: () => void;
  onEdit: () => void;
}

export const PartnerCodeDetails: React.FC<PartnerCodeDetailsProps> = ({ code }) => {
  const [copiedUrl, setCopiedUrl] = React.useState(false);
  const [copiedCode, setCopiedCode] = React.useState(false);

  const partnerUrl = `${window.location.origin}/${code.type === "partner" ? "partners" : "events"}/${code.code}`;

  const copyToClipboard = async (text: string, type: "url" | "code") => {
    await navigator.clipboard.writeText(text);
    if (type === "url") {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } else {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  return (
    <>
      <DialogHeader className="flex flex-row items-center justify-between">
        <div>
          <DialogTitle>Partner Code Details</DialogTitle>
          <p className="mt-1 text-sm text-app-grey-1">Full analytics and information</p>
        </div>
      </DialogHeader>

      <div className="mt-6 space-y-6">
        <div className="rounded-xl bg-app-menu-color border border-app-menu-color-hover">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Code</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium text-app-grey-0">{code.code}</span>
                <WithReset
                  onClick={() => copyToClipboard(code.code, "code")}
                  className="p-1 rounded-lg hover:bg-app-menu-color-hover transition-colors"
                >
                  <InlineSVG
                    src={copiedCode ? "/assets/check-simple.svg" : "/assets/Copy.svg"}
                    className={cn("h-4 w-4 [&_path]:fill-app-grey-2 animate-in fade-in")}
                  />
                </WithReset>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">URL</span>
              <div className="flex items-center gap-2">
                <a
                  href={partnerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-app-primary-cta hover:text-app-primary-cta-hover transition-colors flex items-center gap-1"
                >
                  <span className="max-w-xs truncate">{partnerUrl}</span>
                  <InlineSVG
                    src="/assets/out.svg"
                    className="h-3 w-3 [&_path]:fill-app-primary-cta"
                  />
                </a>
                <WithReset
                  onClick={() => copyToClipboard(partnerUrl, "url")}
                  className="p-1 rounded-lg hover:bg-app-menu-color-hover transition-colors"
                >
                  <InlineSVG
                    src={copiedUrl ? "/assets/check-simple.svg" : "/assets/Copy.svg"}
                    className={cn("h-4 w-4 [&_path]:fill-app-grey-2 animate-in fade-in")}
                  />
                </WithReset>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Type</span>
              <span
                className={cn("inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold", code.type === "partner" ? "bg-blue-500/10 text-blue-500" : "bg-purple-500/10 text-purple-500")}
              >
                {code.type.charAt(0).toUpperCase() + code.type.slice(1)}
              </span>
            </div>

            {code.metadata?.partner_name && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-app-grey-1">Partner Name</span>
                <span className="text-sm font-medium text-app-grey-0">{code.metadata.partner_name}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Status</span>
              <span
                className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold", code.is_active ? "bg-green-500/10 text-green-500" : "bg-app-grey-3/30 text-app-grey-1")}
              >
                <span className={cn("w-1.5 h-1.5 rounded-full", code.is_active ? "bg-green-500" : "bg-app-grey-2")} />
                {code.is_active ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Bonus Credits</span>
              <span className="text-sm font-medium text-app-grey-0">{formatNumber(code.credit_amount)} credits</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Clicks</span>
              <span className="text-sm font-medium text-app-grey-0">{formatNumber(code.click_count)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Signups</span>
              <span className="text-sm font-medium text-app-grey-0">{formatNumber(code.signup_count)}</span>

              {code.click_count > 0 && <p className="text-xs text-app-grey-2 mt-1">{(code.click_count > 0 ? (code.signup_count / code.click_count) * 100 : 0).toFixed(1)}% of clicks</p>}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Conversions</span>
              <span className="text-sm font-medium text-app-grey-0">{formatNumber(code.conversion_count)}</span>

              {code.click_count > 0 && <p className="text-xs text-app-grey-2 mt-1">{((code.conversion_count / code.signup_count) * 100).toFixed(1)}% of signups</p>}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Credits Granted</span>
              <span className="text-sm font-medium text-app-grey-0">{formatNumber(code.credits_granted)}</span>

              {code.click_count > 0 && <p className="text-xs text-app-grey-2 mt-1">${((code.credits_granted / 1000) * 0.01).toFixed(2)} value</p>}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Notes</span>
              <span className="text-sm font-medium text-app-grey-0">{code.notes}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Created</span>
              <div>
                <span className="text-sm font-medium text-app-grey-0">{moment(code.created_at).format("MMM D, YYYY [at] h:mm A")}</span>
                {code.created_by && <p className="text-xs text-app-grey-2">by {code.created_by}</p>}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-app-grey-1">Last Updated</span>
              <span className="text-sm font-medium text-app-grey-0">{moment(code.updated_at).format("MMM D, YYYY [at] h:mm A")}</span>
            </div>

            {Object.keys(code.metadata || {}).length > 0 && (
              <div className="flex items-start justify-between flex-col">
                <span className="text-sm text-app-grey-1">Metadata</span>
                <pre className="text-xs text-app-grey-0 max-h-32 pt-2 w-full overflow-auto text-left">{JSON.stringify(code.metadata, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
